import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';


const ProjectDetailContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  background: rgb(var(--bg));
  min-height: 100vh;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 4rem;
  }

  @media (max-width: 480px) {
    padding: 4.5rem 1rem 3rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2.75rem;
  padding: 2.25rem 1.5rem 1.9rem;
  border-radius: 22px;
  background: rgb(var(--fg) / 0.035);
  color: rgb(var(--fg));
  border: 1px solid rgb(var(--fg) / 0.06);
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba(29, 185, 84, 0.15), transparent 45%),
      radial-gradient(circle at 82% 22%, rgba(29, 185, 84, 0.08), transparent 35%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 480px) {
    margin-bottom: 2rem;
    padding: 1.75rem 1rem 1.4rem;
    border-radius: 18px;
  }
`;

const HeaderTextBlock = styled.div`
  max-width: 900px;
  margin: 0 auto 1.5rem;
`;

const Title = styled.h1`
  font-size: 2.7rem;
  color: rgb(var(--fg) / 0.95);
  margin-bottom: 0.4rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.15rem;
  }

  @media (max-width: 480px) {
    font-size: 1.85rem;
  }
`;

const Date = styled.p`
  font-size: 0.9rem;
  color: rgb(var(--gray) / 0.7);
  letter-spacing: 0.02em;

  @media (max-width: 768px) {
    font-size: 0.82rem;
  }
`;

const TagsContainer = styled.div`
  margin-top: 0;
  display: flex;
  justify-content: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-bottom: 0;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  color: rgb(var(--fg) / 0.9);
  padding: 0.48rem 0.95rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid rgba(29, 185, 84, 0.3);
  backdrop-filter: blur(4px);
  transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: rgb(var(--fg) / 0.08);
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgb(0 0 0 / calc(0.3 * var(--shadow-mul)));
  }

  @media (max-width: 768px) {
    font-size: 0.72rem;
  }
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-width: 860px;
  height: 360px;
  object-fit: cover;
  object-position: center;
  margin: 1.5rem auto 1rem;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  &:hover {
    filter: brightness(1) saturate(1);
  }

  @media (max-width: 768px) {
    height: 260px;
  }

  @media (max-width: 480px) {
    height: 200px;
    max-width: 100%;
  }
`;

const Section = styled.div`
  margin-bottom: 1.75rem;
  padding: 1.6rem 1.45rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));

  @media (max-width: 480px) {
    margin-bottom: 1.25rem;
    padding: 1.2rem 1rem;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.45rem;
  color: #1db954;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(29, 185, 84, 0.25);
  padding-bottom: 0.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.18rem;
    padding-bottom: 0.6rem;
  }
`;

const Text = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--fg) / 0.82);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 0.96rem;
  }

  @media (max-width: 480px) {
    font-size: 0.92rem;
    line-height: 1.75;
  }
`;

const List = styled.ul`
  padding-left: 1.35rem;
  list-style-type: disc;
  color: rgb(var(--fg) / 0.82);
  margin: 0.5rem 0 0;

  @media (max-width: 480px) {
    padding-left: 1.1rem;
  }
`;

const ListItem = styled.li`
  margin-bottom: 0.72rem;
  line-height: 1.7;
  padding-left: 0.1rem;

  &::marker {
    color: #1db954;
  }

  @media (max-width: 768px) {
    margin-bottom: 0.55rem;
  }
`;

const NotebookEmbed = styled.iframe`
  width: 100%;
  height: 800px;
  border: none;
  border-radius: 16px;
  box-shadow: 0 16px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  margin: 1.5rem 0;
  background: rgb(var(--fg) / 0.88);
  border: 1px solid rgb(var(--fg) / 0.08);

  @media (max-width: 480px) {
    height: 400px;
    margin: 1.2rem 0;
    border-radius: 14px;
  }
`;

const CTABox = styled.div`
  margin: 1.25rem 0 1.5rem;
  padding: 1.15rem 1.2rem;
  border-radius: 18px;
  background: rgb(var(--fg) / 0.04);
  border: 1px solid rgba(29, 185, 84, 0.25);
  box-shadow: 0 12px 28px rgb(0 0 0 / calc(0.35 * var(--shadow-mul)));

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 16px;
  }
`;

const CTATitle = styled.h3`
  margin: 0 0 0.45rem 0;
  font-size: 1.02rem;
  font-weight: 800;
  color: #1db954;
`;

const CTAText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.75;
  color: rgb(var(--fg) / 0.82);

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const CTALink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.9rem;
  padding: 0.78rem 1.25rem;
  border-radius: 999px;
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.92);
  border: 1px solid rgb(var(--fg) / 0.14);
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(29, 185, 84, 0.5);
  }
`;

const Video = styled.video`
  width: 100%;
  height: auto;
  margin: 1.5rem 0 0;
  border-radius: 16px;
  box-shadow: 0 16px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  display: block;
  background: rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);

  @media (max-width: 480px) {
    margin: 1.2rem 0 0;
    border-radius: 14px;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2rem auto 0;
  padding: 0.9rem 2rem;
  min-width: 220px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(29, 185, 84, 0.5);
  }

  @media (max-width: 768px) {
    padding: 0.85rem 1.5rem;
    font-size: 0.78rem;
  }

  @media (max-width: 480px) {
    width: 100%;
    min-width: 0;
    padding: 0.8rem 1.2rem;
    font-size: 0.75rem;
  }
`;

const SpotifyAPIProject = () => {
  const tags = ['Python', 'APIs', 'Automation', 'Data Engineering'];
  const datePublished = '2024-08-15';
  const navigate = useNavigate();


  return (
    <ProjectDetailContainer>
      <Header>
        <HeaderTextBlock>
          <Title>Spotify Monthly Playlist Automation</Title>
          <Date>Published on: {datePublished}</Date>
        </HeaderTextBlock>

        <CoverImage src="/images/monthly_playlists.jpg" alt="Spotify Monthly Playlists" />

        <TagsContainer>
          {tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
        </TagsContainer>
      </Header>
      <Section>
        <SectionTitle>Overview</SectionTitle>
        <Text>
          A personal “Spotify Wrapped on demand” system that combines data engineering, user analytics, and lightweight automation. It processes several years of streaming history, structures it into month‑level behavioural insights, and generates playlists and branded covers that visualize how listening patterns evolve over time.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Features</SectionTitle>
        <List>
          <ListItem>Creates monthly “Wrapped‑style” playlists from extended streaming history.</ListItem>
          <ListItem>Transforms raw timestamp‑level listening data into structured insights (month-by-month behavioural profiles).</ListItem>
          <ListItem>Generates custom high‑contrast playlist covers using radial gradients and noise textures.</ListItem>
          <ListItem>Uses deterministic color rules to encode each year with a consistent visual identity.</ListItem>
          <ListItem>Automatically selects readable text color (black/white) based on background luminance.</ListItem>
          <ListItem>Supports optional playlist exclusion to clean the input data.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Why This Project Matters</SectionTitle>
        <Text>
          Although personal in origin, this project reflects the core principles of analytics work: structuring messy data, turning behaviour into measurable patterns, and designing systems that automate repeatable logic. It also required balancing API limits, performance, and deterministic rules — the same considerations behind scalable analytics pipelines.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Dependencies</SectionTitle>
        <List>
          <ListItem>Python 3.7+</ListItem>
          <ListItem>Spotipy for Spotify API interaction</ListItem>
          <ListItem>Pandas and NumPy for data manipulation</ListItem>
          <ListItem>Pillow for image creation and manipulation</ListItem>
          <ListItem>dotenv for environment variable management</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Workflow</SectionTitle>
        <Text>
          The workflow runs through two notebooks: one acts as a lightweight ETL pipeline that cleans, joins, and aggregates listening history into month‑level summaries; the second handles the cover‑generation layer, combining gradient algorithms, luminance rules, and font‑fitting logic to produce consistent branded outputs.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Notebooks</SectionTitle>
        <CTABox>
          <CTATitle>Explore the project code</CTATitle>
          <CTAText>
            This project lives inside a broader repository of API-based experiments and automations. Open the GitHub directory to view the notebooks, supporting scripts, and related project files.
          </CTAText>
          <CTALink
            href="https://github.com/kyriakos-papadopoulos/Projects/tree/main/API_Projects/Spotify/Monthly_Playlists"
            target="_blank"
            rel="noopener noreferrer"
          >
            View GitHub Directory
          </CTALink>
        </CTABox>
        <NotebookEmbed 
          src="/embeds/wrapped_months.html"
          title="Jupyter Notebook"
        />
        <hr /> 
        <NotebookEmbed
          src="/embeds/cover_generation.html"
          title="Jupyter Notebook"
        />
      </Section>

      <Section>
        <SectionTitle>Results</SectionTitle>
        <Video controls>
          <source src="/videos/spotify_demo.mov" type="video/mp4" />
          <source src="/videos/spotify_demo.mov" type="video/quicktime" />
          Your browser does not support the video tag.
        </Video>
      </Section>

      <Section>
        <SectionTitle>Known Issues</SectionTitle>
        <Text>
          Requires the Extended Streaming History dataset — the standard Spotify export does not include track IDs, which makes playlist creation unreliable.
          This limitation mirrors real-world data availability issues, where system design must adapt to the structure and constraints of upstream datasets.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Future Plans</SectionTitle>
        <Text>
          Future improvements include automated cover uploads (pending Spotify API support), more flexible segmentation (weekly, seasonal, or thematic clusters), and packaging the entire workflow as a CLI tool for reproducible analytics pipelines.
        </Text>
      </Section>

      <Button onClick={() => navigate('/projects')}>Back to Projects</Button>
    </ProjectDetailContainer>
  );
};

// Export metadata for use in the Projects page
export const spotifyAPIMetadata = {
  title: 'Spotify Monthly Playlist Automation',
  description: 'A data-engineering style project that transforms multi‑year streaming history into monthly analytics, automated playlists, and custom visualizations.',
  tags: ['Python', 'APIs', 'Automation', 'Data Engineering'],
  languages: ['Python', 'GitHub'],
  datePublished: '2024-08-15',
  path: '/projects/monthly-playlists',
  coverImage: '/images/monthly_playlists.jpg'
};

export default SpotifyAPIProject;